'use client';
import type { ReactElement } from 'react';
import { GraphNodeShapeKind, HEXAGON_CLIP_PATH, SELECTED_SILHOUETTE_COLOR, type GraphNodeShapeDescriptor, type GraphNodeSvgBodyArgs, } from '../Cmarkdowngraphcontrols';
import { FlowchartNodeShape } from './parse-mermaid-flowchart';
/**
 * Maps each of the 10 flowchart shapes to a {@link GraphNodeShapeDescriptor} for
 * {@link GraphNodeFrame}, so the selection glow + accent border follow the real
 * silhouette of every shape (no rectangle behind a diamond — issue #1):
 *
 *  - Rounded shapes (process, terminator pill, connector circle) → BorderRadius.
 *  - Straight-edged polygons (decision, input/output, hexagon, trapezoid) →
 *    ClipPath (two-layer accent + inset fill).
 *  - Curved/struck bodies (database cylinder, document page, predefined process)
 *    → SvgBody, which strokes its own outline so the border follows the path.
 */
const DIAMOND_CLIP = 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)';
const PARALLELOGRAM_CLIP = 'polygon(22% 0%, 100% 0%, 78% 100%, 0% 100%)';
const TRAPEZOID_CLIP = 'polygon(18% 0%, 82% 0%, 100% 100%, 0% 100%)';
const PROCESS_RADIUS = 14;
const CONNECTOR_RADIUS = '50%';
const TERMINATOR_RADIUS = 9999;
/** Stroke width for the SVG bodies, kept crisp with non-scaling-stroke. */
const SVG_STROKE_WIDTH = 1.5;
const SVG_STROKE_WIDTH_SELECTED = 1.75;
/** Keeps the SVG body outline a constant device width regardless of node scale. */
const VECTOR_EFFECT_NON_SCALING = 'non-scaling-stroke';
/** Resolve the stroke color for an SVG body (white silhouette when selected). */
function svgStroke(args: GraphNodeSvgBodyArgs): string {
    return args.isSelected ? SELECTED_SILHOUETTE_COLOR : args.accentColor;
}
function svgStrokeWidth(args: GraphNodeSvgBodyArgs): number {
    return args.isSelected ? SVG_STROKE_WIDTH_SELECTED : SVG_STROKE_WIDTH;
}
/** Database cylinder — an ellipse-capped tube; the border follows the path. */
function renderCylinderBody(args: GraphNodeSvgBodyArgs): ReactElement {
    const { width, height } = args;
    const capRy = Math.min(10, height * 0.18);
    const inset = 1.5;
    const top = inset + capRy;
    const bottom = height - inset - capRy;
    return (<svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block', overflow: 'visible' }} aria-hidden>
      <path d={`M ${inset} ${top} V ${bottom} A ${width / 2 - inset} ${capRy} 0 0 0 ${width - inset} ${bottom} V ${top}`} fill={args.fill} stroke={svgStroke(args)} strokeWidth={svgStrokeWidth(args)} vectorEffect={VECTOR_EFFECT_NON_SCALING}/>
      <ellipse cx={width / 2} cy={top} rx={width / 2 - inset} ry={capRy} fill={args.fill} stroke={svgStroke(args)} strokeWidth={svgStrokeWidth(args)} vectorEffect={VECTOR_EFFECT_NON_SCALING}/>
    </svg>);
}
/** Document — a card with a wavy bottom edge; the border follows the wave. */
function renderDocumentBody(args: GraphNodeSvgBodyArgs): ReactElement {
    const { width, height } = args;
    const inset = 1.5;
    const left = inset;
    const right = width - inset;
    const top = inset;
    const waveTop = height - inset - 14;
    const quarter = (right - left) / 4;
    return (<svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block', overflow: 'visible' }} aria-hidden>
      <path d={`M ${left} ${top} H ${right} V ${waveTop} Q ${right - quarter} ${waveTop + 16} ${left + 2 * quarter} ${waveTop} T ${left} ${waveTop} Z`} fill={args.fill} stroke={svgStroke(args)} strokeWidth={svgStrokeWidth(args)} strokeLinejoin="round" vectorEffect={VECTOR_EFFECT_NON_SCALING}/>
    </svg>);
}
/** Predefined process — a rounded rect double-struck with vertical accent bars. */
function renderPredefinedProcessBody(args: GraphNodeSvgBodyArgs): ReactElement {
    const { width, height } = args;
    const inset = 1.5;
    const radius = 6;
    const bar = Math.min(10, width * 0.12);
    const stroke = svgStroke(args);
    const strokeWidth = svgStrokeWidth(args);
    return (<svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block', overflow: 'visible' }} aria-hidden>
      <rect x={inset} y={inset} width={width - inset * 2} height={height - inset * 2} rx={radius} fill={args.fill} stroke={stroke} strokeWidth={strokeWidth} vectorEffect={VECTOR_EFFECT_NON_SCALING}/>
      <line x1={inset + bar} y1={inset} x2={inset + bar} y2={height - inset} stroke={stroke} strokeWidth={strokeWidth} vectorEffect={VECTOR_EFFECT_NON_SCALING} opacity={0.75}/>
      <line x1={width - inset - bar} y1={inset} x2={width - inset - bar} y2={height - inset} stroke={stroke} strokeWidth={strokeWidth} vectorEffect={VECTOR_EFFECT_NON_SCALING} opacity={0.75}/>
    </svg>);
}
const BORDER_RADIUS_BY_SHAPE: Partial<Record<FlowchartNodeShape, number | string>> = {
    [FlowchartNodeShape.Process]: PROCESS_RADIUS,
    [FlowchartNodeShape.Terminator]: TERMINATOR_RADIUS,
    [FlowchartNodeShape.Connector]: CONNECTOR_RADIUS,
};
const CLIP_PATH_BY_SHAPE: Partial<Record<FlowchartNodeShape, string>> = {
    [FlowchartNodeShape.Decision]: DIAMOND_CLIP,
    [FlowchartNodeShape.InputOutput]: PARALLELOGRAM_CLIP,
    [FlowchartNodeShape.Hexagon]: HEXAGON_CLIP_PATH,
    [FlowchartNodeShape.Trapezoid]: TRAPEZOID_CLIP,
};
const SVG_BODY_BY_SHAPE: Partial<Record<FlowchartNodeShape, (args: GraphNodeSvgBodyArgs) => ReactElement>> = {
    [FlowchartNodeShape.Database]: renderCylinderBody,
    [FlowchartNodeShape.Document]: renderDocumentBody,
    [FlowchartNodeShape.PredefinedProcess]: renderPredefinedProcessBody,
};
/**
 * Resolve a shape descriptor for the frame. Exhaustive over the 10 shapes by
 * construction: every shape is in exactly one of the three lookup maps above;
 * the Process fallback only guards an impossible gap (and satisfies the type).
 */
export function getFlowchartShapeDescriptor(shape: FlowchartNodeShape): GraphNodeShapeDescriptor {
    const borderRadius = BORDER_RADIUS_BY_SHAPE[shape];
    if (borderRadius !== undefined) {
        return { kind: GraphNodeShapeKind.BorderRadius, borderRadius };
    }
    const clipPath = CLIP_PATH_BY_SHAPE[shape];
    if (clipPath !== undefined) {
        return { kind: GraphNodeShapeKind.ClipPath, clipPath };
    }
    const renderBody = SVG_BODY_BY_SHAPE[shape];
    if (renderBody !== undefined) {
        return { kind: GraphNodeShapeKind.SvgBody, renderBody };
    }
    return { kind: GraphNodeShapeKind.BorderRadius, borderRadius: PROCESS_RADIUS };
}
/** Whether a shape's silhouette can carry the inset accent ring (BorderRadius only). */
export function shapeSupportsInsetRings(shape: FlowchartNodeShape): boolean {
    return BORDER_RADIUS_BY_SHAPE[shape] !== undefined;
}
